import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaChartPie, FaClipboardList, FaTruck, FaCheckCircle, 
  FaCog, FaUser, FaSignOutAlt, FaEnvelope, FaBell,
  FaSearch, FaQuestionCircle, FaPlusCircle, FaHistory,
  FaFileInvoice, FaUpload, FaDollarSign, FaClock,
  FaChartLine, FaBoxes, FaUsers, FaBuilding,
  FaMoneyBillWave, FaShieldAlt, FaGavel, FaUserCog,
  FaFileAlt,  // ✅ Quotations icon
  FaMapMarkerAlt,  // ✅ Live Map icon
  FaAddressBook,  // ✅ CRM icon
  FaCalendarAlt,  // ✅ Bookings icon
  FaTruckMoving,  // ✅ Fleet Overview icon
  FaLifeRing,  // ✅ Support icon
  FaFolderOpen,  // ✅ Project Management icon
} from 'react-icons/fa';
import NotificationBell from '../components/NotificationBell';
import MessagesDropdown from '../components/MessagesDropdown';
import AdminProfileDropdown from '../components/AdminProfileDropdown';
import SupportModal from '../components/SupportModal';
import { admin, subbieInvoices } from '../api';

function AdminLayout({ children, activeTab, onLogout, user }) {
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  const sidebarRef = React.useRef(null);
  const [counts, setCounts] = useState({
    activeJobs: 0,
    pendingJobs: 0,
    loads: 0,
    pendingPayments: 0,
    pendingApprovals: 0
  });
  const [supportOpen, setSupportOpen] = useState(false);

  const handleSidebarScroll = () => {
    if (sidebarRef.current) {
      sessionStorage.setItem('adminSidebarScroll', sidebarRef.current.scrollTop);
    }
  };

  useEffect(() => {
    if (sidebarRef.current) {
      const savedScroll = sessionStorage.getItem('adminSidebarScroll');
      if (savedScroll) {
        sidebarRef.current.scrollTop = parseInt(savedScroll);
      }
    }
  });

  useEffect(() => {
    if (currentUser.id) {
      fetchCounts();
    }
  }, [currentUser.id]);

  const fetchCounts = async () => {
    try {
      const statsData = await admin.getDashboardStats();
      if (statsData.success) {
        setCounts(prev => ({
          ...prev,
          activeJobs: statsData.activeJobs || 0,
          pendingJobs: statsData.pendingJobs || 0,
          loads: statsData.loads || 0
        }));
      }

      try {
        const invData = await subbieInvoices.getAll();
        if (invData.success) {
          const pending = invData.invoices?.filter(i => i.status === 'pending_payment').length || 0;
          setCounts(prev => ({
            ...prev,
            pendingPayments: pending
          }));
        }
      } catch (invErr) {
        console.error('Error fetching pending payments:', invErr);
      }

    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const getActiveBg = (tab) => {
    return activeTab === tab ? '#00A86B' : 'transparent';
  };

  const getActiveColor = (tab) => {
    return activeTab === tab ? '#0A1929' : '#94A3B8';
  };

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      <div 
        ref={sidebarRef}
        onScroll={handleSidebarScroll}
        className="w-64 text-white fixed h-full overflow-y-auto" 
        style={{ backgroundColor: '#0A1929' }}
      >
        <div className="p-6 border-b" style={{ borderColor: '#1E2F3F' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg" style={{ backgroundColor: '#00A86B', color: '#0A1929' }}>
              <span className="text-sm">OV</span>
            </div>
            <div className="font-bold text-lg">
              ONE<span style={{ color: '#00A86B' }}>VIEW</span>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>Admin Portal</p>
          {currentUser?.full_name && (
            <p className="text-xs mt-1 font-medium" style={{ color: '#00A86B' }}>{currentUser.full_name}</p>
          )}
        </div>

        <nav className="p-4">
          {/* ===== MAIN ===== */}
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Main</p>
          
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('dashboard'), color: getActiveColor('dashboard') }}>
            <FaChartPie size={18} /> Dashboard
          </Link>
          
          <Link to="/new-jobs" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('newjobs'), color: getActiveColor('newjobs') }}>
            <FaClipboardList size={18} /> New Jobs
            {counts.pendingJobs > 0 && <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#F59E0B', color: '#0A1929' }}>{counts.pendingJobs}</span>}
          </Link>
          
          <Link to="/active-jobs" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('activejobs'), color: getActiveColor('activejobs') }}>
            <FaTruck size={18} /> Active Jobs
            {counts.activeJobs > 0 && <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#3B82F6', color: 'white' }}>{counts.activeJobs}</span>}
          </Link>
          
          <Link to="/completed-jobs" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('completed'), color: getActiveColor('completed') }}>
            <FaCheckCircle size={18} /> Completed Jobs
          </Link>

          <Link to="/loads" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('loads'), color: getActiveColor('loads') }}>
            <FaBoxes size={18} /> Loads
            {counts.loads > 0 && <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#F59E0B', color: '#0A1929' }}>{counts.loads}</span>}
          </Link>

          <Link to="/live-map" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('livemap'), color: getActiveColor('livemap') }}>
            <FaMapMarkerAlt size={18} /> Live Map
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          {/* ===== MANAGEMENT ===== */}
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Management</p>
          
          <Link to="/quotes" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('quotes'), color: getActiveColor('quotes') }}>
            <FaFileAlt size={18} /> Quotations
          </Link>

          <Link to="/quotes/register" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('quoteregister'), color: getActiveColor('quoteregister') }}>
            <FaClock size={18} /> Quote Register
          </Link>
          
          <Link to="/clients" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('clients'), color: getActiveColor('clients') }}>
            <FaBuilding size={18} /> Clients
          </Link>
          
          <Link to="/subcontractors" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('subcontractors'), color: getActiveColor('subcontractors') }}>
            <FaUsers size={18} /> Subcontractors
          </Link>

          <Link to="/fleet-overview" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('fleet-overview'), color: getActiveColor('fleet-overview') }}>
            <FaTruckMoving size={18} /> Fleet Overview
          </Link>

          <Link to="/admin-scheduler" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('admin-scheduler'), color: getActiveColor('admin-scheduler') }}>
            <FaCalendarAlt size={18} /> Subbie Scheduling
          </Link>

          <Link to="/admin/pod-approvals" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('pod-approvals'), color: getActiveColor('pod-approvals') }}>
            <FaCheckCircle size={18} /> POD Approvals
          </Link>

          <Link to="/tender-dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('tenders'), color: getActiveColor('tenders') }}>
            <FaGavel size={18} /> Tenders
          </Link>

          <Link to="/admin/uploads" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('uploads'), color: getActiveColor('uploads') }}>
            <FaUpload size={18} /> Uploads
          </Link>

          <Link to="/users" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('users'), color: getActiveColor('users') }}>
            <FaUserCog size={18} /> Users
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>

          {/* ===== CRM ===== */}
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>CRM</p>

          <Link to="/crm/onboarding" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('crm/onboarding'), color: getActiveColor('crm/onboarding') }}>
            <FaClipboardList size={18} /> Onboarding
          </Link>
          <Link to="/crm/deals" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('crm/deals'), color: getActiveColor('crm/deals') }}>
            <FaDollarSign size={18} /> Deals
          </Link>
          <Link to="/crm/contacts" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('crm/contacts'), color: getActiveColor('crm/contacts') }}>
            <FaAddressBook size={18} /> Contacts
          </Link>
          <Link to="/crm/bookings" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('crm/bookings'), color: getActiveColor('crm/bookings') }}>
            <FaCalendarAlt size={18} /> Bookings
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          {/* ===== PROJECTS ===== */}
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Projects</p>

          <Link to="/project-management" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('projects'), color: getActiveColor('projects') }}>
            <FaFolderOpen size={18} /> Project Management
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>

          {/* ===== ACCOUNTING ===== */}
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Accounting</p>
          
          <Link to="/admin-payments" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('payments'), color: getActiveColor('payments') }}>
            <FaMoneyBillWave size={18} /> Subbie Payments
            {counts.pendingPayments > 0 && <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#EF4444', color: 'white' }}>{counts.pendingPayments}</span>}
          </Link>
          
          <Link to="/suppliers" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('suppliers'), color: getActiveColor('suppliers') }}>
            <FaTruckMoving size={18} /> Suppliers
          </Link>
          
          <Link to="/accounts-overview" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('accounts'), color: getActiveColor('accounts') }}>
            <FaChartPie size={18} /> Accounts Overview
          </Link>
          
          <Link to="/invoices" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('invoices'), color: getActiveColor('invoices') }}>
            <FaFileInvoice size={18} /> Invoices
          </Link>
          
          <Link to="/reports" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('reports'), color: getActiveColor('reports') }}>
            <FaChartLine size={18} /> Reports
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          {/* ===== ACCOUNT ===== */}
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Account</p>
          
          <Link to="/profile" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('profile'), color: getActiveColor('profile') }}>
            <FaUser size={18} /> Profile
          </Link>
          
          <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('settings'), color: getActiveColor('settings') }}>
            <FaCog size={18} /> Settings
          </Link>

          <Link to="/audit-log" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('audit'), color: getActiveColor('audit') }}>
            <FaHistory size={18} /> Audit Log
          </Link>

          {currentUser?.role === 'super_admin' && (
            <Link to="/system-management" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('system'), color: getActiveColor('system') }}>
              <FaShieldAlt size={18} /> System Management
            </Link>
          )}

          <Link to="/help" className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ backgroundColor: getActiveBg('help'), color: getActiveColor('help') }}>
            <FaQuestionCircle size={18} /> Help
          </Link>

          <button
            onClick={() => setSupportOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer"
            style={{ color: '#94A3B8' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#00A86B'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
          >
            <FaLifeRing size={18} /> Support
          </button>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer" style={{ color: '#94A3B8' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}>
            <FaSignOutAlt size={18} /> Logout
          </button>
        </nav>
      </div>

      <div className="ml-64 flex-1 min-w-0 max-w-full">
        <div className="border-b px-8 py-4 flex justify-between items-center sticky top-0 z-10" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: '#4A5568' }}>{new Date().toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-3">
            <MessagesDropdown userId={currentUser?.id} />
            <NotificationBell userId={currentUser?.id} />
            <AdminProfileDropdown user={currentUser} onLogout={onLogout} />
          </div>
        </div>
        <div className="p-8">
          {children}
        </div>
      </div>
      <SupportModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

export default AdminLayout;